import React, { useState, useEffect } from 'react';
// import fixOrientation from 'fix-orientation';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';

import { getTrophyStyle } from '.';
import { colors } from 'global/colors';
import { Button } from 'components/Button/RoundButton';
import * as strings from './strings';
import { useAppContext } from 'hooks/useContext';
import apiErrors from 'api/apiErrors';
import XPLevel from 'components/XPLevel/XPLevel';
import {
  Container,
  UserContainer,
  UserWrapper,
  AvatarContainer,
  Avatar,
  AvatarInput,
  Icon,
  UserTextWrapper,
  UserInfoWrapper,
  UserName,
  InfoContainer,
  InfoLabelContainer,
  InfoLabel,
  Email,
  RightContainer,
  SkillWrapper,
  LoadingIcon,
  InfoSection,
  InfoSectionWrapper,
  InfoSectionHeader,
  InfoSectionContent,
  InfoSectionContainer,
  InfoSectionLabel,
  ActivityDivider,
  ActivityMainContainer,
  SkillHeader,
  Circle,
  TrophyWrapper,
  TrophyContainer,
  TrophyTitle,
  TrophyIcon,
  TrophyAmount,
  MaxAchievementsText,
  LatestAchievementContainer,
  LatestAchievementText,
  ActivityWrapper,
  ActivityContainer,
  ActivityTitle,
  AchievementWrapper,
  EmailUpdatesLabelAndCheckbox,
  EmailUpdatesInputCheckbox,
  EmailUpdatesLabel,
} from './styles';

const trophyTypes = [
  { value: 'gold', label: 'Gold' },
  { value: 'silver', label: 'Silver' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'iron', label: 'Iron' },
];

export default React.memo(({ setProfile, goToAchievements, goToXP, updateUser, profile, hideEditButton }) => {
  const {
    coderCommunityProfile,
    level,
    xpForLevel,
    totalXp,
    xpInThisLevel,
    trophies,
    latestCompletedAchievement,
    activity,
    avatar,
    email,
    maxAchievements,
    achievements,
    emailUpdates,
  } = profile;

  let { country, role, employer, description, languages, frameworks, name } = coderCommunityProfile;
  if (!name) name = profile.name;

  const xpPercent = Math.round((xpInThisLevel / xpForLevel) * 100 * 2) / 2;

  const [avatarLoading, setAvatarLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const { error, loading } = useSelector((state) => state.profile);
  const [currentAvatar, setCurrentAvatar] = useState(null);
  const [oldAvatar, setOldAvatar] = useState(null);
  const { language } = useAppContext();

  useEffect(() => {
    if (error) {
      toast.error(apiErrors?.[error?.name]?.[language] ?? strings.imageError?.[language] ?? error);
      setIsEditing(true);
      setCurrentAvatar(oldAvatar);
      setProfile('avatar', oldAvatar, true);
    }
  }, [error, language, oldAvatar, setProfile]);

  useEffect(() => {
    setUserName(name);
  }, [name]);

  useEffect(() => {
    setCurrentAvatar(avatar);
    if (!currentAvatar) {
      setOldAvatar(avatar);
    }
  }, [avatar, currentAvatar]);

  const levelBlock = (
    <XPLevel
      level={level}
      xpPercent={xpPercent}
      xpInThisLevel={xpInThisLevel}
      xpForLevel={xpForLevel}
      totalXp={totalXp}
      onClick={() => goToXP()}
    />
  );

  const TrophyItem = ({ value, label, color, icon, description }) => {
    return (
      <TrophyContainer key={value}>
        <TrophyTitle>{label}</TrophyTitle>
        <TrophyIcon
          data-tip={description}
          rgb={color}
          icon={icon}
          data-tooltip-id="trophyTip"
          data-tooltip-html={description}
          data-tooltip-variant="info"
        />
        <TrophyAmount>{trophies[value]}</TrophyAmount>
      </TrophyContainer>
    );
  };

  const trophiesBlock = (
    <ActivityMainContainer>
      <SkillHeader>Trophies</SkillHeader>
      <TrophyWrapper>
        {trophyTypes.map((trophyType, index) => {
          const { color, icon, description } = getTrophyStyle(trophyType.value);
          return <TrophyItem description={description} color={color} icon={icon} {...trophyType} key={index} />;
        })}
      </TrophyWrapper>
    </ActivityMainContainer>
  );

  const achievementsBlock = (
    <ActivityMainContainer onClick={() => goToAchievements()}>
      <SkillHeader>Achievements</SkillHeader>
      <AchievementWrapper>
        <Circle color={colors.darkRed}>
          {achievements}
          <MaxAchievementsText>of {maxAchievements}</MaxAchievementsText>
        </Circle>
        <LatestAchievementContainer>
          {latestCompletedAchievement.name && (
            <LatestAchievementText>Latest: {latestCompletedAchievement.name}</LatestAchievementText>
          )}
        </LatestAchievementContainer>
      </AchievementWrapper>
    </ActivityMainContainer>
  );

  const activityBlock = (
    <ActivityMainContainer>
      <SkillHeader>Activity</SkillHeader>
      <ActivityWrapper>
        {Object.keys(activity).map((act, index) => (
          <React.Fragment key={act}>
            <ActivityContainer>
              <ActivityTitle>{act}</ActivityTitle>
              {activity[act].map((trophy, innerIndex) => {
                if (innerIndex >= 5) {
                  return null;
                }
                const { color, icon } = getTrophyStyle(trophy.trophyType);
                return (
                  <TrophyIcon
                    key={`${index}-${innerIndex}`}
                    data-tip={trophy.contestName}
                    style={{ marginTop: innerIndex < 4 ? '3px' : 0 }}
                    icon={icon}
                    rgb={color}
                    size={24}
                  />
                );
              })}
            </ActivityContainer>
            {index !== Object.keys(activity).length - 1 && <ActivityDivider />}
          </React.Fragment>
        ))}
      </ActivityWrapper>
    </ActivityMainContainer>
  );

  const textarea = document.getElementById('textarea');
  if (textarea) {
    textarea.setAttribute('style', 'height: 0px');
    textarea.setAttribute('style', 'height:' + textarea.scrollHeight + 'px');
  }

  const infoSection = (
    <InfoSectionWrapper>
      <InfoSection full>
        <InfoSectionHeader>About me</InfoSectionHeader>
        {!isEditing && !description && <span style={{ marginRight: '8px' }}>-</span>}
        {(description || isEditing) && (
          <InfoSectionContent
            id="textarea"
            editing={isEditing.toString()}
            onChange={(e) => setProfile('description', e.target.value)}
            value={description || ''}
          />
        )}
      </InfoSection>
      <InfoSection half>
        <InfoSectionHeader>
          Languages
          {isEditing && <Icon icon="\f067" onClick={() => setProfile('languages', [...languages, ''])} />}
        </InfoSectionHeader>
        {!isEditing && !languages?.length && <span style={{ marginRight: '8px' }}>-</span>}
        {languages?.map((language, index) => (
          <InfoSectionContainer key={index}>
            <InfoSectionLabel
              isEditing={isEditing}
              placeholder="Language"
              onChange={(e) => {
                const langs = [...languages];
                langs.splice(index, 1, e.target.value);
                setProfile('languages', langs);
              }}
              value={language}
            />
            {isEditing && (
              <Icon
                icon="\f068"
                style={{ position: 'absolute', right: '0' }}
                onClick={() => {
                  const langs = [...languages];
                  langs.splice(index, 1);
                  setProfile('languages', langs);
                }}
              />
            )}
          </InfoSectionContainer>
        ))}
      </InfoSection>
      <InfoSection half>
        <InfoSectionHeader>
          Frameworks
          {isEditing && <Icon icon="\f067" onClick={() => setProfile('frameworks', [...frameworks, ''])} />}
        </InfoSectionHeader>
        {!isEditing && !frameworks?.length && <span style={{ marginRight: '8px' }}>-</span>}
        {frameworks?.map((framework, index) => (
          <InfoSectionContainer key={index}>
            <InfoSectionLabel
              isEditing={isEditing}
              placeholder="Framework"
              onChange={(e) => {
                const framews = [...frameworks];
                framews.splice(index, 1, e.target.value);
                setProfile('frameworks', framews);
              }}
              value={framework}
            />
            {isEditing && (
              <Icon
                icon="\f068"
                style={{ position: 'absolute', right: '0' }}
                onClick={() => {
                  const framews = [...frameworks];
                  framews.splice(index, 1);
                  setProfile('frameworks', framews);
                }}
              />
            )}
          </InfoSectionContainer>
        ))}
        {!hideEditButton && (
          <EmailUpdatesLabelAndCheckbox>
            <EmailUpdatesInputCheckbox
              type="checkbox"
              disabled={!isEditing}
              checked={emailUpdates}
              onChange={() => setProfile('emailUpdates', !emailUpdates, true)}
            />
            <EmailUpdatesLabel>Receive email updates</EmailUpdatesLabel>
          </EmailUpdatesLabelAndCheckbox>
        )}
      </InfoSection>
    </InfoSectionWrapper>
  );

  return (
    <Container>
      <Tooltip id="trophyTip" place="top" className="custom-tooltip" />
      <UserContainer>
        <UserWrapper>
          <AvatarContainer>
            <Avatar image={currentAvatar} isLoading={avatarLoading} isEditing={isEditing}>
              {avatarLoading && <LoadingIcon />}
              {isEditing && (
                <AvatarInput
                  name="avatarInput"
                  onChange={(e) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(e.target.files[0]);
                    e.target.value = null;
                    reader.onprogress = (e) => {
                      switch (e.target.readyState) {
                        case 0:
                          console.log('ERROR');
                          break;
                        case 1:
                          setAvatarLoading(true);
                          break;
                        default:
                          setAvatarLoading(false);
                          break;
                      }
                    };
                    reader.onload = () => {
                      setProfile('avatar', reader.result, true);
                      setAvatarLoading(false);
                      // fixOrientation(reader.result, {}, (fixed) => {
                      //   setProfile('avatar', fixed, true);
                      //   setAvatarLoading(false);
                      // });
                    };
                    reader.onerror = (error) => {
                      console.log('Error: ', error);
                    };
                  }}
                />
              )}
            </Avatar>
          </AvatarContainer>
          <UserTextWrapper>
            <UserInfoWrapper>
              <UserName
                isEditing={isEditing}
                placeholder="Name"
                onBlur={(e) => {
                  if (!e.target.value || !e.target.value.trim()) {
                    setUserName(name);
                  } else {
                    setUserName(e.target.value);
                    setProfile('name', e.target.value.trim());
                  }
                }}
                onChange={(e) => setUserName(e.target.value)}
                value={userName}
              />
              {(email || isEditing) && (
                <Email
                  isEditing={false}
                  placeholder="Email"
                  onChange={(e) => setProfile('email', e.target.value)}
                  value={email}
                />
              )}
            </UserInfoWrapper>
            <InfoContainer>
              <InfoLabelContainer>
                <Icon icon="\f0ac" />
                {!isEditing && !country && <span style={{ marginRight: '8px' }}>-</span>}
                {(isEditing || country) && (
                  <InfoLabel
                    isEditing={isEditing}
                    placeholder="Country"
                    onChange={(e) => setProfile('country', e.target.value)}
                    value={country || ''}
                  />
                )}
              </InfoLabelContainer>
              <InfoLabelContainer>
                <Icon icon="\f007" />
                {!isEditing && !role && <span style={{ marginRight: '8px' }}>-</span>}
                {(isEditing || role) && (
                  <InfoLabel
                    isEditing={isEditing}
                    placeholder="Occupation"
                    onChange={(e) => setProfile('role', e.target.value)}
                    value={role || ''}
                  />
                )}
              </InfoLabelContainer>
              <InfoLabelContainer>
                <Icon icon="\f0c0" />
                {!isEditing && !employer && <span style={{ marginRight: '8px' }}>-</span>}
                {(isEditing || employer) && (
                  <InfoLabel
                    isEditing={isEditing}
                    placeholder="Employer"
                    onChange={(e) => setProfile('employer', e.target.value)}
                    value={employer || ''}
                  />
                )}
              </InfoLabelContainer>
            </InfoContainer>
          </UserTextWrapper>
        </UserWrapper>
        {infoSection}
        {loading ? (
          <Icon spin={true} icon={'\f110'} />
        ) : hideEditButton ? (
          ''
        ) : (
          <Button
            primaryBlue
            onClick={async () => {
              setIsEditing(!isEditing);
              if (isEditing) {
                let updatedLanguages = [];
                if (languages)
                  languages.map((lang) => {
                    if (lang && lang.trim() !== '') {
                      updatedLanguages.push(lang.trim());
                    }
                    return null;
                  });
                let updatedFrameWorks = [];
                if (frameworks)
                  frameworks.map((framework) => {
                    if (framework && framework.trim() !== '') {
                      updatedFrameWorks.push(framework.trim());
                    }
                    return null;
                  });
                if (country && country.trim() === '') setProfile('country', '');
                if (employer && employer.trim() === '') setProfile('employer', '');
                if (role && role.trim() === '') setProfile('role', '');
                if (description && description.trim() === '') setProfile('description', '');
                setProfile('languages', updatedLanguages);
                setProfile('frameworks', updatedFrameWorks);
                let event = await updateUser(profile);
                if (event && event.amount) toast.success(event.description);
              }
            }}
            style={{ marginTop: '5px' }}
          >
            {isEditing ? 'Save' : 'Edit'}
          </Button>
        )}
      </UserContainer>
      <RightContainer>
        <SkillWrapper>{achievementsBlock}</SkillWrapper>
        <SkillWrapper>
          {levelBlock}
          {trophiesBlock}
        </SkillWrapper>
        <SkillWrapper>{activityBlock}</SkillWrapper>
        {/* <ChallengeTab profile={profile} /> */}
      </RightContainer>
    </Container>
  );
});
